import React, { useEffect, useRef, useState } from 'react'
import { useThemeContext } from '../../../Context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { DARKCOLORS, LIGHTCOLORS } from '../../../Utils/constants';
import { Box, Button, Typography, TablePagination } from '@mui/material';
import LoadingAnimation from '../../../Components/Common/LoadingAnimation';
import SuccessNotification from '../../../Components/Common/SuccessNotification';
import { getTempRecords } from '../../../Services/authService';
import * as XLSX from "xlsx";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import TransactionsTable from '../../Views/Transcations/TransactionsTable';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import dayjs from 'dayjs';
import { formatDate, formatTime } from '../../../Components/Common/dateFormat';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import '../../../Styles/daterange-responsive.css';
import { DateRange } from 'react-date-range';

function TempReadings({ machineId, machineType }) {
    const { mode } = useThemeContext();
    const { t } = useTranslation();
    const calendarRef = useRef(null);
    const calendarButtonRef = useRef(null);

    const borderColor1 = mode === 'dark' ? DARKCOLORS.BaseBlack : LIGHTCOLORS.BaseBlack;
    const buttonBackgroundColor = mode === 'dark' ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;
    const btnColor1 = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
    const containerBackground = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
    const dropIcon = mode === 'dark' ? DARKCOLORS.GreyScale80 : LIGHTCOLORS.GreyScale80;
    const btnColor = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const textColor = mode === 'dark' ? DARKCOLORS.BodyLightBackground : LIGHTCOLORS.BodyLightBackground;

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [dateRange, setDateRange] = useState([
        {
            key: 'selection',
        },
    ]);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
    const [showNoDataNotification, setShowNoDataNotification] = useState(false);

    const handleCalendarToggle = () => {
        if (calendarButtonRef.current) {
            const rect = calendarButtonRef.current.getBoundingClientRect();
            setCalendarPosition({ top: rect.bottom + window.scrollY + 10, left: rect.left + window.scrollX });
        }
        setIsCalendarOpen((prev) => !prev);
    };

    const handleDateChange = (item) => {
        setDateRange([item.selection]);
    };

    // Define columns based on machine type
    const columns =
        machineType === "Pizza Vending Machine"
            ? [
                { field: "date", label: t("membershipCards.Date") },
                { field: "time", label: t("vendingMachines.time") },
                { field: "temp", label: t("vendingMachines.ovenTemp") }, // changed here
                { field: "status", label: t("vendingMachines.status") },
            ]
            : [
                { field: "date", label: t("membershipCards.Date") },
                { field: "time", label: t("vendingMachines.time") },
                { field: "maxTemp", label: t("vendingMachines.maxTemp") },
                { field: "minTemp", label: t("vendingMachines.minTemp") },
                { field: "temp", label: t("vendingMachines.liveTemp1") },
                { field: "status", label: t("vendingMachines.status") },
            ];


    useEffect(() => {
        fetchTempRecords();
    }, [machineId, page, rowsPerPage]);

    const fetchTempRecords = async (rangeOverride) => {
        setLoading(true);
        try {
            let from;
            let to;
            const range = rangeOverride !== undefined ? rangeOverride : dateRange;
            if (range.length > 0 && range[0].startDate && range[0].endDate) {
                from = dayjs(range[0].startDate).format("YYYY-MM-DD");
                to = dayjs(range[0].endDate).format("YYYY-MM-DD");
            }

            const tempData = await getTempRecords(machineId, page, rowsPerPage, from, to);
            setTotalRows(tempData?.totalElements || 0);
            setRows(tempData?.content.map((row) => ({
                ...row,
                date: formatDate(row.dateTime),
                time: formatTime(row.dateTime)
            })) || []);
        } catch (err) {
            console.error("Fetch failed", err);
        } finally {
            setLoading(false);
        }
    };

    const handleApply = () => {
        fetchTempRecords();
        setIsCalendarOpen(false)
    };

    const handleReset = () => {
        setDateRange([{ key: 'selection' }]);
        setIsCalendarOpen(false);
        fetchTempRecords([]);
    };

    // Close the calendar when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(event.target) &&
                calendarButtonRef.current &&
                !calendarButtonRef.current.contains(event.target)
            ) {
                setIsCalendarOpen(false);
            }
        }

        if (isCalendarOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isCalendarOpen]);

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle individual row selection
    const handleRowSelect = (row) => {
        if (selectedRows.some((selectedRow) => selectedRow.id === row.id)) {
            setSelectedRows(selectedRows.filter((selectedRow) => selectedRow.id !== row.id));
        } else {
            setSelectedRows([...selectedRows, row]);
        }
    };

    // Handle "Select All"
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedRows([]); // Deselect all
        } else {
            setSelectedRows(rows); // Select all
        }
        setSelectAll(!selectAll);
    };

    // Handle Export to Excel
    const handleExport = async () => {
        // ponytail: early exit if page has no data and nothing is selected
        const hasNoPageData = !rows || rows.length === 0;
        const hasNoSelection = !selectedRows || selectedRows.length === 0;
        if (hasNoPageData && hasNoSelection) {
            setShowNoDataNotification(true);
            return;
        }

        let exportData;

        // If user specifically selected rows, export those
        if (selectedRows?.length > 0) {
            exportData = selectedRows;
        } else {
            // ponytail: fetch all records respecting date range via backend-safe pagination loop
            let from, to;
            if (dateRange.length > 0 && dateRange[0].startDate && dateRange[0].endDate) {
                from = dayjs(dateRange[0].startDate).format("YYYY-MM-DD");
                to = dayjs(dateRange[0].endDate).format("YYYY-MM-DD");
            }

            try {
                const size = 100; // backend max page size
                let allContent = [];
                let currentPage = 0;
                let totalElements = 0;

                do {
                    const pageData = await getTempRecords(machineId, currentPage, size, from, to);
                    totalElements = pageData?.totalElements || 0;
                    const content = pageData?.content || [];
                    if (content.length === 0) break;
                    allContent = [...allContent, ...content];
                    currentPage++;
                } while (allContent.length < totalElements);

                exportData = allContent.map(row => ({
                    ...row,
                    date: formatDate(row.dateTime),
                    time: formatTime(row.dateTime)
                }));
            } catch (err) {
                console.error("Export failed", err);
                return;
            }
        }

        if (!exportData || exportData.length === 0) {
            setShowNoDataNotification(true);
            return;
        }

        // English to German header mapping
        const headerMapping = {
            date: t("membershipCards.Date"),
            time: t("vendingMachines.time"),
            maxTemp: t("vendingMachines.maxTemp"),
            minTemp: t("vendingMachines.minTemp"),
            temp: t("vendingMachines.liveTemp"),
            status: t("vendingMachines.status"),
        };

        // Transform data to use German headers
        const transformedData = exportData.map(row => {
            const newRow = {};
            Object.keys(headerMapping).forEach(key => {
                newRow[headerMapping[key]] = row[key];
            });
            return newRow;
        });

        // Create worksheet and workbook
        const worksheet = XLSX.utils.json_to_sheet(transformedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Mitgliedskarten");

        // Save file with timestamp
    const timestamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14);
const fileName = t("vendingMachines.temp_readings_export_file_name", "TempReadings");

XLSX.writeFile(workbook, `${fileName}_${timestamp}.xlsx`);
    };

    return (
        <>
            <div className={`dashboard-container-overview ${mode === 'dark' ? 'dark-mode' : ''}`}>
                {loading && <LoadingAnimation />}
                <Box sx={{ display: { xs: 'block', sm: 'flex', md: 'flex' }, justifyContent: 'space-between', alignItems: 'center' }}>

                    <Box sx={{ width: "50%" }}>
                        <h2 className='membership-text-heading' style={{ width: '100%' }}>{t('vendingMachines.previousTempCond')}</h2>

                    </Box>

                    <Box className="temp-calender-sec" sx={{ display: 'flex', width: '40%', height:'auto', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
                       
                        {/* Calendar Icon */}
                        <Button
                            ref={calendarButtonRef}
                            aria-label="select-date"
                            onClick={handleCalendarToggle}
                            sx={{
                                background: btnColor,
                                borderRadius: "50px",
                                padding: "8px 14px",
                                color: dropIcon,
                                textTransform: 'none',
                                display: "flex",
                                height: "40px !important",
                                alignItems: "center !important",
                                gap: "8px",
                            }}
                        >
                            <Typography sx={{ fontSize: "13px", color: textColor, fontWeight: 500 }}>
                                {dateRange[0]?.startDate && dateRange[0]?.endDate
                                    ? `${dayjs(dateRange[0].startDate).format('DD MMM YYYY')} - ${dayjs(dateRange[0].endDate).format('DD MMM YYYY')}`
                                    : t('sales_analytics.select_date_range', 'Select Date Range')}
                            </Typography>
                            <CalendarTodayOutlinedIcon style={{ fontSize: "1rem" }} />
                        </Button>
                       
                       
                        <Button className="export-btn" style={{
                            fontSize: "13px", margin: "12px", background: buttonBackgroundColor, color: borderColor1, minWidth: "105px",
                            padding: "9.5px 26px", textTransform: "none", borderRadius: "24px"
                        }} onClick={handleExport} endIcon={<FileUploadOutlinedIcon />}>
                            
                            {t('membershipCards.export')}
                        
                        </Button>
                       
                        {/* Date Range Picker */}
                        {isCalendarOpen && (
                            <div id='calender-res' ref={calendarRef} style={{
                                background: containerBackground,
                                position: 'absolute',
                                top: `${calendarPosition.top}px`,
                                right: `20px`,
                                zIndex: 1000, display: "flex", flexDirection: "column", borderRadius: "12px",
                                '@media (max-width: 600px)': {
                                    left: '-6px !important'
                                },
                            }}>
                                <DateRange
                                    editableDateInputs={true}
                                    onChange={handleDateChange}
                                    moveRangeOnFirstSelection={false}
                                    ranges={dateRange}
                                    style={{
                                        borderRadius: '12px',
                                        padding: '8px',
                                    }}
                                />
                                <div style={{ textAlign: "right", margin: "1rem 0.5rem", display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                                    <button className="cancel-btn" type="button" style={{ color: btnColor1, borderColor: btnColor1, padding: "10px 15px", fontSize: "15px" }} onClick={handleReset}>{t('sales_analytics.reset', 'Reset')}</button>
                                    <button type="button" style={{ background: btnColor1, padding: "10px 15px", fontSize: "15px" }} className='save-btn' onClick={handleApply}>{t('sales_analytics.apply')}</button>
                                </div>
                            </div>
                        )}
                        <Box />

                    </Box>
                </Box>

                {loading ? (
                    <p> {t('vendingMachines.Loading')}</p>
                ) : (
                    <>
                        <TransactionsTable
                            rows={rows || []}
                            columns={columns || []}
                            onRowSelect={handleRowSelect}
                            onSelectAll={handleSelectAll}
                            selectedRows={selectedRows || []}
                            selectAll={selectAll}
                        />
                        <TablePagination
                            component="div"
                            count={totalRows}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center', // Center the pagination controls
                                alignItems: 'center',
                            }}
                        />
                    </>
                )}
            </div>
            <SuccessNotification
                open={showNoDataNotification}
                message={t('discounts.Nodataexport')}
                severity="warning"
                onClose={() => setShowNoDataNotification(false)}
            />
        </>
    )
}

export default TempReadings